import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, signal, ViewChild } from '@angular/core';
import { Globals } from 'src/app/globals';
import { GenericIqAnalysisSummaryAccordinComponent } from './generic-iq-analysis-summary-accordin/generic-iq-analysis-summary-accordin.component';
import moment from 'moment';
import { IssuesService } from 'src/app/core/service/api/issues.service';
import { StakeholdersService } from 'src/app/core/service/api/stakeholders.service';
import { ContextService } from 'src/app/core/service/api/context.service';
import { catchError, concatMap, from, map, of, toArray } from 'rxjs';
import { showFeedbackSaved } from 'src/app/core/util/notification';
import { MatDialogRef } from '@angular/material/dialog';
import { IqSummaryModalComponent } from '../iq-summary-modal/iq-summary-modal.component';
import { uniqueArray } from 'src/app/core/util/uniqueArray';
import { mergeDuplicateContexts } from 'src/app/core/util/mergeDuplicateContexts';

@Component({
  selector: 'app-iq-analysis-summary',
  templateUrl: './iq-analysis-summary.component.html',
  styleUrl: './iq-analysis-summary.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IqAnalysisSummaryComponent {
  @ViewChild('issuesAccordin') issuesAccordin!: GenericIqAnalysisSummaryAccordinComponent;
  @ViewChild('stakeholdersAccordin') stakeholdersAccordin!: GenericIqAnalysisSummaryAccordinComponent;
  @ViewChild('contextsAccordin') contextsAccordin!: GenericIqAnalysisSummaryAccordinComponent;
  @Input() contextAnalysisData: any = {};
  loaded = signal(false);
  isProcessing = signal(false);
  expandedItems: boolean[] = [];
  allIssues: any[] = [];
  allStakeholders: any[] = [];
  createdStakeholders: any[] = [];
  createdIssues: any[] = [];
  createdContexts: any[] = [];
  updatedContexts: any[] = [];
  data: any = [];
  contextTypes: any = [];
  
  constructor(public globals: Globals, private dialogRef: MatDialogRef<IqSummaryModalComponent>, private cdRef: ChangeDetectorRef, private _issuesService: IssuesService, private _stakeholdersService: StakeholdersService, private _contextsService: ContextService) {}

  ngOnInit(): void {
    this._contextsService.getContextTypes().subscribe((res)=>{
      this.contextTypes = res;
    })
    // this.data = this.contextAnalysisData.map((item, index) => ({id: index+1, ...item}));
    // this.data = uniqueArray(this.data, 'text');
    this.data = mergeDuplicateContexts(this.contextAnalysisData, 'text');
    this.aggregateIssuesAndStakeholders();
    this.loaded.set(true);
  }

  toggleItem(index: number) {
    this.expandedItems[index] = !this.expandedItems[index];
  }

  aggregateIssuesAndStakeholders() {
    this.allIssues = this.data.flatMap((ctx: any) =>
      ctx?.issues ?? []
    ).map((issue: any, index: number) => ({
      ...issue,
      id: index + 1
    }));
    this.allStakeholders = this.data.flatMap((ctx: any) =>
        ctx?.stakeholders ?? []
    ).map((stk: any, index: number) => ({
      ...stk,
      id: index + 1
    }));
    this.allIssues = uniqueArray(this.allIssues, 'name');
    this.allStakeholders = uniqueArray(this.allStakeholders, 'name');
  }

  onContextItemDeclined(declinedItem: any) {
    const declinedIssueIds = declinedItem?.issues?.map((i: any) => i.id);
    const declinedStakeholderIds = declinedItem?.stakeholders?.map((s: any) => s.id);
    // remove stakeholders and issues
    this.issuesAccordin?.removeItemsByIds(declinedIssueIds);
    this.stakeholdersAccordin?.removeItemsByIds(declinedStakeholderIds);
    this.cdRef.detectChanges();
  }

  preparePayloads() {
    this.isProcessing.set(true);
    const stakeholdersPayload = this.stakeholdersAccordin?.itemsData.map((item: any) => ({
      name: item.name,
      description: item.description,
      locations: item.locations || {},
      address: item.address || ''
    })) || [];

    const issuesPayload = this.issuesAccordin?.itemsData.map((item: any) => ({
      name: item.name,
      description: item.description,
      locations: item.locations || {},
      issues_date: new Date(),
      urgency: 'middle',
      issues_status: 'open',
      impact: 'low',
      deadline: moment(new Date()).add(1, 'months').toDate(),
      issues_score: 41.66
    })) || [];

    const contextsPayload = this.contextsAccordin?.itemsData.map((item: any) => ({
      text: item.name || item.text ,
      description: item.description,
      types: this.contextTypes.filter(type => type.name==(item.type || item.context_type)),
      locations: item.locations || {},
      source: item.source || 'app',
      metadata: item.metadata || {}
    })) || [];

    this.submitPreparedPayloadsRx({
      stakeholdersPayload,
      issuesPayload,
      contextsPayload
    });
  }

  submitPreparedPayloadsRx(data) {
    const { stakeholdersPayload, issuesPayload, contextsPayload } = data;
    this.createdStakeholders = [];
    this.createdIssues = [];
    this.createdContexts = [];
    this.updatedContexts = [];
    this.isProcessing.set(true);
    // -> create stakeholders 
    const stakeholders$ = stakeholdersPayload?.length
      ? this._stakeholdersService.createBulkStakeholders(stakeholdersPayload).pipe(
          catchError(err => {
            console.error('Bulk stakeholder creation failed:', err);
            return of([]);
          })
        )
      : of([]); // skip if empty

    stakeholders$
      .pipe(
        concatMap((stakeholdersResponses: any[]) => {
          this.createdStakeholders = stakeholdersResponses || [];
          // -> create issues
          const issues$ = issuesPayload?.length
            ? this._issuesService.creaetBulkIssues(issuesPayload).pipe(
                catchError(err => {
                  console.error('Bulk issue creation failed:', err);
                  return of([]);
                })
              )
            : of([]); // skip if empty

          return issues$.pipe(
            map((issuesResponses: any[]) => ({
              stakeholdersResponses,
              issuesResponses
            }))
          );
        }),
        concatMap((previous: any) => {
          this.createdIssues = previous.issuesResponses || [];

          // -> create contexts
          if (!contextsPayload?.length) {
            return of({
              stakeholdersResponses: previous.stakeholdersResponses,
              issuesResponses: previous.issuesResponses,
              contextsResponses: []
            });
          }

          // Attach created issues & stakeholders to each context before creating
          const enrichedContextsPayload = contextsPayload.map((ctx: any) => {
            const accordinItem = this.contextsAccordin?.itemsData.find(
              (i: any) => i.text === ctx.text || i.name === ctx.name
            );

            if (!accordinItem) return ctx;

            const issueNames = accordinItem.issues?.map((i: any) => i.name) || [];
            const stakeholderNames = accordinItem.stakeholders?.map((s: any) => s.name) || [];

            const attachedIssues = this.createdIssues.filter((ci: any) =>
              issueNames.includes(ci.name)
            );
            const attachedStakeholders = this.createdStakeholders.filter((cs: any) =>
              stakeholderNames.includes(cs.name)
            );

            return {
              ...ctx,
              issues: attachedIssues,
              stakeholders: attachedStakeholders
            };
          });

          // -> Create contexts
          return this._contextsService.createBulkContext(enrichedContextsPayload).pipe(
            catchError(err => {
              console.error('Bulk context creation failed:', err);
              return of([]);
            }),
            map((contextsResponses: any[]) => ({
              stakeholdersResponses: previous.stakeholdersResponses,
              issuesResponses: previous.issuesResponses,
              contextsResponses
            }))
          );
        })
      )
      .subscribe({
        next: (result: any) => {
          this.createdContexts = result.contextsResponses || [];
          showFeedbackSaved();
          this.isProcessing.set(false);
          this.closeModal();
          this.cdRef.markForCheck();
        },
        error: (err) => {
          console.error('Bulk creation failed:', err);
          this.isProcessing.set(false);
        },
        complete: () => {
          console.log('Bulk creation complete (Rx).');
        }
      });
  }

  closeModal(){
    this.dialogRef.close();
  }
}
